import React, { useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';

function SummaryStatsCards({ personalRecords, completionStats }) {
  const stats = useMemo(() => {
    // Calculate total workouts
    const totalWorkouts = completionStats.reduce((sum, month) => sum + month.workout_count, 0);

    // Calculate total time (in hours)
    const totalMinutes = completionStats.reduce((sum, month) => sum + (month.total_minutes || 0), 0);
    const totalHours = Math.round(totalMinutes / 60);

    // Calculate PRs this month
    const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM
    const prsThisMonth = personalRecords.filter(pr => {
      const prMonth = pr.achieved_date.substring(0, 7);
      return prMonth === currentMonth;
    }).length;

    // Calculate current streak (consecutive months with workouts)
    let currentStreak = 0;
    if (completionStats.length > 0) {
      // completionStats comes DESC from backend (newest first)
      const now = new Date();
      const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      // Check if there's activity this month or last month
      const hasRecentActivity = completionStats.some(stat => {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
        const lastYearMonth = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
        return stat.month === currentYearMonth || stat.month === lastYearMonth;
      });

      if (hasRecentActivity) {
        // Count consecutive months with workouts
        for (let i = 0; i < completionStats.length; i++) {
          if (completionStats[i].workout_count > 0) {
            currentStreak++;
          } else {
            break;
          }
        }
      }
    }

    // Calculate average workout duration
    const avgDuration = totalWorkouts > 0 ? Math.round(totalMinutes / totalWorkouts) : 0;

    // Total PRs
    const totalPRs = personalRecords.length;

    return {
      totalWorkouts,
      totalHours,
      prsThisMonth,
      currentStreak,
      avgDuration,
      totalPRs
    };
  }, [personalRecords, completionStats]);

  const StatCard = ({ icon, label, value, unit, variant = 'primary' }) => (
    <Card className="stat-card h-100">
      <Card.Body className="text-center">
        <div className={`stat-icon mb-2 text-${variant}`}>
          <i className={`fas ${icon} fa-2x`}></i>
        </div>
        <h3 className="stat-value mb-1">
          {value}
          {unit && <span className="stat-unit ms-1">{unit}</span>}
        </h3>
        <p className="stat-label text-muted mb-0">{label}</p>
      </Card.Body>
    </Card>
  );

  return (
    <Row className="g-3">
      <Col lg={2} md={4} sm={6}>
        <StatCard
          icon="fa-dumbbell"
          label="Total Workouts"
          value={stats.totalWorkouts}
          variant="primary"
        />
      </Col>
      <Col lg={2} md={4} sm={6}>
        <StatCard
          icon="fa-clock"
          label="Total Time"
          value={stats.totalHours}
          unit="hrs"
          variant="info"
        />
      </Col>
      <Col lg={2} md={4} sm={6}>
        <StatCard
          icon="fa-trophy"
          label="Total PRs"
          value={stats.totalPRs}
          variant="warning"
        />
      </Col>
      <Col lg={2} md={4} sm={6}>
        <StatCard
          icon="fa-star"
          label="PRs This Month"
          value={stats.prsThisMonth}
          variant="success"
        />
      </Col>
      <Col lg={2} md={4} sm={6}>
        <StatCard
          icon="fa-fire"
          label="Current Streak"
          value={stats.currentStreak}
          unit="months"
          variant="danger"
        />
      </Col>
      <Col lg={2} md={4} sm={6}>
        <StatCard
          icon="fa-chart-line"
          label="Avg Duration"
          value={stats.avgDuration}
          unit="min"
          variant="secondary"
        />
      </Col>
    </Row>
  );
}

export default SummaryStatsCards;
